import { Component } from '@theme/component';

/** Fired on the document when a background video's sound is turned on, so others can mute. */
const SOUND_ON_EVENT = 'video-background:sound-on';

/**
 * A custom element that renders a muted, looping video background.
 *
 * The video sources are attached the first time the element nears the viewport, so
 * off-screen videos cost nothing at page load. Playback then follows visibility: the
 * video plays while it is in view, and pauses and mutes when it scrolls out. Optional
 * buttons let the visitor pause the video and turn its sound on; a video the visitor
 * paused stays paused until they play it again.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [playbackButton] - The play/pause button, when controls are rendered.
 * @property {HTMLButtonElement} [soundButton] - The sound button, when controls are rendered.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {AbortController | undefined} */
  #abortController;
  #sourcesLoaded = false;
  #pausedByVisitor = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    for (const type of ['play', 'pause', 'volumechange']) {
      videoElement.addEventListener(type, this.#syncState, { signal });
    }
    document.addEventListener(SOUND_ON_EVENT, this.#handleOtherSoundOn, { signal });
    this.#syncState();

    if (!('IntersectionObserver' in window)) {
      this.#loadSources();
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersection, { rootMargin: '200px 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * Plays or pauses the video at the visitor's request.
   */
  togglePlayback() {
    const { videoElement } = this.refs;

    if (videoElement.paused) {
      this.#pausedByVisitor = false;
      this.#play();
    } else {
      this.#pausedByVisitor = true;
      videoElement.pause();
    }
  }

  /**
   * Turns the video's sound on or off at the visitor's request.
   */
  toggleSound() {
    const { videoElement } = this.refs;

    videoElement.muted = !videoElement.muted;
    if (videoElement.muted) return;

    document.dispatchEvent(new CustomEvent(SOUND_ON_EVENT, { detail: { source: this } }));
    this.#pausedByVisitor = false;
    this.#play();
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const entry = entries.at(-1);
    if (!entry) return;

    const { videoElement } = this.refs;

    if (entry.isIntersecting) {
      if (!this.#pausedByVisitor) this.#play();
      return;
    }

    videoElement.muted = true;
    if (!videoElement.paused) videoElement.pause();
  };

  /**
   * Only one background video has sound at a time.
   * @param {Event} event
   */
  #handleOtherSoundOn = (event) => {
    if (!(event instanceof CustomEvent) || event.detail?.source === this) return;

    this.refs.videoElement.muted = true;
  };

  /** Mirrors the video's state onto the element and the buttons' accessible names. */
  #syncState = () => {
    const { videoElement, playbackButton, soundButton } = this.refs;

    this.toggleAttribute('data-paused', videoElement.paused);
    this.toggleAttribute('data-sound', !videoElement.muted);

    if (playbackButton) {
      const label = videoElement.paused ? playbackButton.dataset.labelPlay : playbackButton.dataset.labelPause;
      if (label) playbackButton.setAttribute('aria-label', label);
    }
    if (soundButton) {
      const label = videoElement.muted ? soundButton.dataset.labelUnmute : soundButton.dataset.labelMute;
      if (label) soundButton.setAttribute('aria-label', label);
    }
  };

  #play() {
    this.#loadSources();
    // Autoplay can be refused, for example in a low-power mode; the poster image stays visible.
    this.refs.videoElement.play().catch(() => {});
  }

  #loadSources() {
    if (this.#sourcesLoaded) return;
    this.#sourcesLoaded = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
